import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
  Fade,
  Slide,
  Zoom,
  Grow,
  styled,
  alpha,
  keyframes
} from '@mui/material';

import { Iconify } from 'src/components/iconify';

// Enhanced Card with Hover Effects
const HoverCard = styled(Card)(({ theme }) => ({
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
    '& .hover-content': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

// Animated Counter
const countUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const AnimatedCounter = styled(Typography)(({ theme }) => ({
  animation: `${countUp} 0.6s ease-out`,
  fontWeight: 'bold',
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
}));

// Skeleton Loading Components
export const StatsCardSkeleton = () => (
  <Card sx={{ p: 2, minHeight: 100 }}>
    <Stack direction="row" alignItems="center" spacing={2}>
      <Skeleton variant="circular" width={40} height={40} />
      <Stack sx={{ flex: 1 }}>
        <Skeleton variant="text" width={80} height={32} />
        <Skeleton variant="text" width={120} height={20} />
      </Stack>
    </Stack>
  </Card>
);

export const TableRowSkeleton = ({ columns = 6 }: { columns?: number }) => (
  <Box sx={{ p: 2 }}>
    {Array.from({ length: 5 }).map((_, index) => (
      <Stack key={index} direction="row" spacing={2} sx={{ mb: 2 }}>
        <Skeleton variant="circular" width={40} height={40} />
        {Array.from({ length: columns - 1 }).map((__, colIndex) => (
          <Skeleton key={colIndex} variant="text" width={120} height={24} />
        ))}
      </Stack>
    ))}
  </Box>
);

// Enhanced Stats Card
interface EnhancedStatsCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  delay?: number;
}

export const EnhancedStatsCard: React.FC<EnhancedStatsCardProps> = ({ 
  title, 
  value, 
  icon, 
  color, 
  delay = 0 
}) => (
  <Grow in timeout={600 + delay}>
    <HoverCard>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${color}.main`,
              color: 'white',
              '& svg': {
                fontSize: 24,
              },
            }}
          >
            {icon}
          </Box>
          <Stack sx={{ flex: 1 }}>
            <AnimatedCounter variant="h4" style={{ animationDelay: `${delay + 200}ms` }}>
              {value}
            </AnimatedCounter>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </HoverCard>
  </Grow>
);

// Tab Panel with Smooth Transitions
interface AnimatedTabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  direction?: 'left' | 'right' | 'up' | 'down';
}

export const AnimatedTabPanel: React.FC<AnimatedTabPanelProps> = ({ 
  children, 
  value, 
  index, 
  direction = 'left' 
}) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && (
      <Slide direction={direction} in={value === index} timeout={500}>
        <Box sx={{ p: { xs: 1, sm: 1.5 } }}>
          <Fade in={value === index} timeout={700}>
            <div>{children}</div>
          </Fade>
        </Box>
      </Slide>
    )}
  </div>
);

// Loading Overlay
export const LoadingOverlay = ({ loading }: { loading: boolean }) => (
  <Fade in={loading}>
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: alpha('#fff', 0.8),
        zIndex: 1000,
      }}
    >
      <Stack alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            border: '3px solid #f3f3f3',
            borderTop: '3px solid #3498db',
            animation: 'spin 2s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        />
        <Typography variant="body2" color="text.secondary">
          Yükleniyor...
        </Typography>
      </Stack>
    </Box>
  </Fade>
);

// Success Animation
export const SuccessAnimation = ({ show, onComplete }: { show: boolean; onComplete?: () => void }) => {
  React.useEffect(() => {
    if (show && onComplete) {
      const timer = setTimeout(onComplete, 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [show, onComplete]);

  return (
    <Zoom in={show}>
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9999,
          bgcolor: 'success.main',
          color: 'white',
          p: 3,
          borderRadius: 2,
          boxShadow: 24,
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              bgcolor: 'success.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Iconify icon="solar:check-circle-bold" width={32} sx={{ color: 'success.dark' }} />
          </Box>
          <Typography variant="h6">İşlem Başarılı!</Typography>
        </Stack>
      </Box>
    </Zoom>
  );
};

// Staggered List Animation
export const StaggeredList = ({ children, delay = 100 }: { children: React.ReactNode[]; delay?: number }) => (
  <Stack spacing={1}>
    {children.map((child, index) => (
      <Grow key={index} in timeout={600 + index * delay}>
        <div>{child}</div>
      </Grow>
    ))}
  </Stack>
); 